import { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Drawer,
  Group,
  Loader,
  MultiSelect,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconAlertTriangle, IconCheck, IconLock, IconSend } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ApiError} from '../../api/client';
import { api, unwrap } from '../../api/client';
import type { MaintenanceEvent } from '../../api/types';
import { useSession } from '../../app/session';
import { AttachmentList } from '../../components/Attachments';
import { RichTextField, RichTextView } from '../../components/RichText';
import { StatusBadge } from '../../components/StatusBadge';
import { formatDate, formatDateTime, fromApiDate, toApiDate, today } from '../../lib/format';
import { applyFieldErrors, notifyError, notifySaved } from '../../lib/notify';

type FormValues = {
  aircraft: string;
  title: string;
  area: string;
  tasks: string[];
  opened_on: Date | null;
  performed_on: Date | null;
  performed_by: string;
  performed_by_organisation: string;
  release_reference: string;
  notes: string;
  findings: string;
  cost: number | '';
  currency: string;
  grounded_aircraft: boolean;
};

/**
 * The maintenance log entry: what was done, by whom, at which counter readings, with notes
 * and files. Completing it rolls the tasks it covers forward and clears their alerts.
 */
export function MaintenanceEventDrawer({
  opened,
  onClose,
  eventId,
  aircraftId,
  defaultTaskIds,
}: {
  opened: boolean;
  onClose: () => void;
  eventId?: number | null;
  aircraftId?: number;
  defaultTaskIds?: number[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { can, enumChoices } = useSession();
  const [comment, setComment] = useState('');

  const event = useQuery({
    queryKey: ['maintenance', 'event', eventId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/maintenance/events/{id}/', { params: { path: { id: eventId! } } }),
      ),
    enabled: opened && Boolean(eventId),
  });

  const aircraftList = useQuery({
    queryKey: ['aircraft', 'options'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/aircraft/', { params: { query: { page_size: 200 } } })),
    enabled: opened,
  });

  const record = event.data;
  const selectedAircraft = record?.aircraft ?? aircraftId;

  const tasks = useQuery({
    queryKey: ['maintenance', 'tasks', selectedAircraft],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/maintenance/tasks/', {
          params: { query: { aircraft: selectedAircraft, page_size: 200 } },
        }),
      ),
    enabled: opened && Boolean(selectedAircraft),
  });

  const people = useQuery({
    queryKey: ['people', 'options'],
    queryFn: async () =>
      unwrap(await api.GET('/api/v1/people/', { params: { query: { page_size: 300 } } })),
    enabled: opened,
  });

  const form = useForm<FormValues>({
    initialValues: {
      aircraft: String(aircraftId ?? ''),
      title: '',
      area: 'AIRFRAME',
      tasks: (defaultTaskIds ?? []).map(String),
      opened_on: fromApiDate(today()),
      performed_on: null,
      performed_by: '',
      performed_by_organisation: '',
      release_reference: '',
      notes: '',
      findings: '',
      cost: '',
      currency: 'EUR',
      grounded_aircraft: false,
    },
    validate: {
      aircraft: (value) => (value ? null : t('common.requiredField')),
      title: (value) => (value.trim() ? null : t('common.requiredField')),
    },
  });

  useEffect(() => {
    if (!record) return;
    form.setValues({
      aircraft: String(record.aircraft),
      title: record.title,
      area: record.area ?? 'AIRFRAME',
      tasks: (record.tasks ?? []).map(String),
      opened_on: fromApiDate(record.opened_on),
      performed_on: fromApiDate(record.performed_on),
      performed_by: record.performed_by ? String(record.performed_by) : '',
      performed_by_organisation: record.performed_by_organisation ?? '',
      release_reference: record.release_reference ?? '',
      notes: record.notes ?? '',
      findings: record.findings ?? '',
      cost: record.cost ? Number(record.cost) : '',
      currency: record.currency ?? 'EUR',
      grounded_aircraft: record.grounded_aircraft ?? false,
    });
    form.resetDirty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record?.id]);

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const body = {
        aircraft: Number(values.aircraft),
        title: values.title.trim(),
        area: values.area as MaintenanceEvent['area'],
        tasks: values.tasks.map(Number),
        opened_on: toApiDate(values.opened_on),
        performed_on: toApiDate(values.performed_on),
        performed_by: values.performed_by ? Number(values.performed_by) : null,
        performed_by_organisation: values.performed_by_organisation.trim(),
        release_reference: values.release_reference.trim(),
        notes: values.notes,
        findings: values.findings,
        cost: values.cost === '' ? null : String(values.cost),
        currency: values.currency,
        grounded_aircraft: values.grounded_aircraft,
      };
      if (eventId) {
        return unwrap(
          await api.PATCH('/api/v1/maintenance/events/{id}/', {
            params: { path: { id: eventId } },
            body,
          }),
        );
      }
      return unwrap(await api.POST('/api/v1/maintenance/events/', { body }));
    },
    onSuccess: async () => {
      notifySaved();
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      await queryClient.invalidateQueries({ queryKey: ['aircraft'] });
      onClose();
    },
    onError: (error: ApiError) => {
      if (!applyFieldErrors(error, form.setErrors)) notifyError(error);
    },
  });

  const complete = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/maintenance/events/{id}/complete/', {
          params: { path: { id: eventId! } },
          body: {
            performed_on: toApiDate(form.values.performed_on ?? new Date()) ?? undefined,
            released_by: form.values.performed_by ? Number(form.values.performed_by) : null,
            release_reference: form.values.release_reference.trim(),
          },
        }),
      ),
    onSuccess: async () => {
      notifySaved(t('action.complete'));
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      await queryClient.invalidateQueries({ queryKey: ['aircraft'] });
      await queryClient.invalidateQueries({ queryKey: ['alerts'] });
      onClose();
    },
    onError: (error: ApiError) => notifyError(error),
  });

  const reopen = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/maintenance/events/{id}/reopen/', {
          params: { path: { id: eventId! } },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['maintenance'] });
    },
    onError: (error: ApiError) => notifyError(error),
  });

  const addComment = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/v1/maintenance/events/{id}/comments/', {
          params: { path: { id: eventId! } },
          body: { event: eventId!, body: comment },
        }),
      ),
    onSuccess: async () => {
      setComment('');
      await queryClient.invalidateQueries({ queryKey: ['maintenance', 'event', eventId] });
    },
    onError: (error: ApiError) => notifyError(error),
  });

  const isDone = record?.status === 'DONE';
  const isLocked = isDone;
  const requiresRelease = (record?.task_details ?? []).some(
    (task) => task.requires_release_to_service,
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Group gap="sm">
          <Title order={3}>{eventId ? t('maintenance.log') : t('maintenance.newEvent')}</Title>
          {record ? <StatusBadge kind="event" value={record.status} /> : null}
        </Group>
      }
    >
      {eventId && event.isLoading ? (
        <Loader />
      ) : (
        <Stack gap="md">
          <form onSubmit={form.onSubmit((values) => save.mutate(values))}>
            <Stack gap="md">
              <Group grow align="flex-start">
                <Select
                  label={t('aircraft.registration')}
                  required
                  disabled={Boolean(eventId) || Boolean(aircraftId)}
                  data={(aircraftList.data?.results ?? []).map((entry) => ({
                    value: String(entry.id),
                    label: `${entry.registration} — ${entry.type_label}`,
                  }))}
                  searchable
                  allowDeselect={false}
                  {...form.getInputProps('aircraft')}
                />
                <Select
                  label={t('maintenance.area')}
                  data={enumChoices('maintenance_area').map((choice) => ({
                    value: choice.value,
                    label: choice.label,
                  }))}
                  allowDeselect={false}
                  disabled={isLocked}
                  {...form.getInputProps('area')}
                />
              </Group>

              <TextInput
                label={t('common.name')}
                required
                disabled={isLocked}
                {...form.getInputProps('title')}
              />

              <MultiSelect
                label={t('maintenance.tasks')}
                description={t('maintenance.dueList')}
                data={(tasks.data?.results ?? []).map((task) => ({
                  value: String(task.id),
                  label: `${task.code} — ${task.name}`,
                }))}
                searchable
                disabled={isLocked}
                {...form.getInputProps('tasks')}
              />

              <Group grow align="flex-start">
                <DateInput
                  label={t('maintenance.performedOn')}
                  valueFormat="DD.MM.YYYY"
                  clearable
                  disabled={isLocked}
                  {...form.getInputProps('performed_on')}
                />
                <Select
                  label={t('maintenance.performedBy')}
                  data={(people.data?.results ?? []).map((person) => ({
                    value: String(person.id),
                    label: person.full_name,
                  }))}
                  searchable
                  clearable
                  disabled={isLocked}
                  {...form.getInputProps('performed_by')}
                />
              </Group>

              <Group grow align="flex-start">
                <TextInput
                  label={t('maintenance.releasedBy')}
                  placeholder="CAMO / Part-145"
                  disabled={isLocked}
                  {...form.getInputProps('performed_by_organisation')}
                />
                <TextInput
                  label={t('maintenance.releaseReference')}
                  disabled={isLocked}
                  {...form.getInputProps('release_reference')}
                />
              </Group>

              <RichTextField
                label={t('common.notes')}
                value={form.values.notes}
                onChange={(html) => form.setFieldValue('notes', html)}
                placeholder={t('maintenance.log')}
              />

              <RichTextField
                label={t('maintenance.findings')}
                value={form.values.findings}
                onChange={(html) => form.setFieldValue('findings', html)}
                minHeight={120}
              />

              <Group grow align="flex-start">
                <NumberInput
                  label={t('maintenance.cost')}
                  min={0}
                  decimalScale={2}
                  disabled={isLocked}
                  {...form.getInputProps('cost')}
                />
                <TextInput
                  label={t('maintenance.currency')}
                  w={100}
                  disabled={isLocked}
                  {...form.getInputProps('currency')}
                />
              </Group>

              <Checkbox
                label={t('maintenance.groundsAircraft')}
                disabled={isLocked}
                {...form.getInputProps('grounded_aircraft', { type: 'checkbox' })}
              />

              {record?.counter_values && Object.keys(record.counter_values).length ? (
                <Card p="sm">
                  <Text fz="xs" tt="uppercase" c="dimmed" fw={600} mb={4}>
                    {t('maintenance.countersAtWork')}
                  </Text>
                  <Group gap="md">
                    {Object.entries(record.counter_values as Record<string, string>).map(
                      ([key, value]) => (
                        <Group key={key} gap={4}>
                          <Text fz="xs" c="dimmed">
                            {key}
                          </Text>
                          <Text fz="sm" fw={600} ff="monospace">
                            {value}
                          </Text>
                        </Group>
                      ),
                    )}
                  </Group>
                </Card>
              ) : null}

              {requiresRelease && !isDone ? (
                <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
                  {t('maintenance.requiresRelease')}
                </Alert>
              ) : null}

              <Group justify="space-between">
                <Group gap="xs">
                  {eventId && !isDone && can('maintenance.close_event') ? (
                    <Button
                      color="teal"
                      leftSection={<IconCheck size={16} />}
                      loading={complete.isPending}
                      onClick={() => complete.mutate()}
                    >
                      {t('action.complete')}
                    </Button>
                  ) : null}
                  {eventId && isDone && can('maintenance.close_event') ? (
                    <Button
                      variant="default"
                      leftSection={<IconLock size={16} />}
                      loading={reopen.isPending}
                      onClick={() => reopen.mutate()}
                    >
                      {t('action.reopen')}
                    </Button>
                  ) : null}
                </Group>
                <Group gap="xs">
                  <Button variant="default" onClick={onClose}>
                    {t('action.cancel')}
                  </Button>
                  <Button type="submit" loading={save.isPending}>
                    {t('action.save')}
                  </Button>
                </Group>
              </Group>
            </Stack>
          </form>

          {eventId ? (
            <>
              <Divider label={t('maintenance.attachments')} labelPosition="left" />
              <AttachmentList
                target="maintenance.maintenanceevent"
                targetId={eventId}
                canEdit={can('attachments.add')}
              />

              <Divider label={t('maintenance.comments')} labelPosition="left" />
              <Stack gap="sm">
                {(record?.comments ?? []).map((entry) => (
                  <Card key={entry.id} p="sm">
                    <Group justify="space-between" mb={4}>
                      <Text fz="sm" fw={600}>
                        {entry.author_name || t('staff.person')}
                      </Text>
                      <Text fz="xs" c="dimmed">
                        {formatDateTime(entry.created_at)}
                      </Text>
                    </Group>
                    <RichTextView html={entry.body} />
                    {entry.attachments.length ? (
                      <Group gap={4} mt={6}>
                        {entry.attachments.map((attachment) => (
                          <Badge
                            key={attachment.id}
                            size="xs"
                            variant="outline"
                            component="a"
                            href={attachment.file}
                            target="_blank"
                            style={{ cursor: 'pointer' }}
                          >
                            {attachment.filename}
                          </Badge>
                        ))}
                      </Group>
                    ) : null}
                  </Card>
                ))}

                {can('maintenance.add_event') ? (
                  <Stack gap="xs">
                    <RichTextField
                      value={comment}
                      onChange={setComment}
                      minHeight={100}
                      placeholder={t('maintenance.addComment')}
                    />
                    <Button
                      size="compact-sm"
                      leftSection={<IconSend size={14} />}
                      disabled={!comment || comment === '<p></p>'}
                      loading={addComment.isPending}
                      onClick={() => addComment.mutate()}
                      style={{ alignSelf: 'flex-start' }}
                    >
                      {t('maintenance.addComment')}
                    </Button>
                  </Stack>
                ) : null}
              </Stack>

              {record?.performed_on ? (
                <Text fz="xs" c="dimmed">
                  {t('maintenance.performedOn')}: {formatDate(record.performed_on)}
                  {record.performed_by_name ? ` · ${record.performed_by_name}` : ''}
                  {record.released_by_name
                    ? ` · ${t('maintenance.releasedBy')}: ${record.released_by_name}`
                    : ''}
                </Text>
              ) : null}
            </>
          ) : null}
        </Stack>
      )}
    </Drawer>
  );
}
